/**
 * Adapter that wraps a folder FileList to match JSZip's interface,
 * so the Facebook parser can handle both ZIP files and unzipped folders
 * (e.g., when Safari auto-extracts the archive).
 */
import JSZip from 'jszip';

interface ZipLikeEntry {
  dir: boolean;
  async(type: 'string'): Promise<string>;
  async(type: 'blob'): Promise<Blob>;
  async(type: string): Promise<string | Blob>;
}

function createAsyncFn(file: File) {
  function asyncFn(type: 'string'): Promise<string>;
  function asyncFn(type: 'blob'): Promise<Blob>;
  function asyncFn(type: string): Promise<string | Blob>;
  function asyncFn(type: string): Promise<string | Blob> {
    if (type === 'string') {
      return file.text();
    }
    return Promise.resolve(file.slice());
  }
  return asyncFn;
}

export type ZipLike = Pick<JSZip, 'files'> & {
  files: Record<string, ZipLikeEntry>;
};

/**
 * Build a JSZip-compatible object from a folder FileList.
 * Each File's webkitRelativePath provides the relative path.
 */
export function folderToZipLike(files: FileList): ZipLike {
  const entries: Record<string, ZipLikeEntry> = {};

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const path = file.webkitRelativePath || file.name;

    entries[path] = {
      dir: false,
      async: createAsyncFn(file),
    };
  }

  return { files: entries } as ZipLike;
}

/**
 * Check if a drop event contains a folder (using webkitGetAsEntry).
 * Returns the FileList if it's a folder, null otherwise.
 */
export async function getDroppedFolderFiles(
  dataTransfer: DataTransfer,
): Promise<FileList | null> {
  const items = dataTransfer.items;
  if (!items || items.length === 0) return null;

  const entry = items[0].webkitGetAsEntry?.();
  if (!entry || !entry.isDirectory) return null;

  const files = await readDirectoryRecursive(entry as FileSystemDirectoryEntry);
  return createFileList(files);
}

async function readDirectoryRecursive(
  dirEntry: FileSystemDirectoryEntry,
  basePath = '',
): Promise<File[]> {
  const files: File[] = [];
  const reader = dirEntry.createReader();
  const currentPath = basePath ? `${basePath}/${dirEntry.name}` : dirEntry.name;

  let entries: FileSystemEntry[] = [];
  // readEntries may return results in batches
  let batch: FileSystemEntry[];
  do {
    batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    entries = entries.concat(batch);
  } while (batch.length > 0);

  for (const entry of entries) {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        (entry as FileSystemFileEntry).file(resolve, reject);
      });
      // Reconstruct webkitRelativePath since the File API doesn't set it for dropped folders
      const relativePath = `${currentPath}/${entry.name}`;
      const patchedFile = new File([file], file.name, { type: file.type, lastModified: file.lastModified });
      Object.defineProperty(patchedFile, 'webkitRelativePath', { value: relativePath });
      files.push(patchedFile);
    } else if (entry.isDirectory) {
      const subFiles = await readDirectoryRecursive(entry as FileSystemDirectoryEntry, currentPath);
      files.push(...subFiles);
    }
  }

  return files;
}

function createFileList(files: File[]): FileList {
  const dt = new DataTransfer();
  // DataTransfer doesn't preserve webkitRelativePath, so we return a pseudo-FileList
  return Object.assign(files, {
    item: (i: number) => files[i] || null,
    length: files.length,
    [Symbol.iterator]: files[Symbol.iterator].bind(files),
  }) as unknown as FileList;
}

/**
 * Build ZipLike from dropped folder files (with reconstructed paths).
 */
export function droppedFilesToZipLike(files: File[]): ZipLike {
  const entries: Record<string, ZipLikeEntry> = {};

  for (const file of files) {
    const path = (file as any).webkitRelativePath || file.name;

    entries[path] = {
      dir: false,
      async: createAsyncFn(file),
    };
  }

  return { files: entries } as ZipLike;
}
